import { CallAPIPostPromise } from "Common/commaon";
import {
  AlertCircle, ArrowDownToLine, CheckCircle2, Clock3, CreditCard,
  ChevronLeft, ChevronRight, FileText, ReceiptText, RefreshCw, Search, WalletCards,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import "./contractorInvoices.css";
import api from "Common/api";
import type { BillingAccessState } from "components/BillingRecoveryModal";

interface InvoiceMetadata {
  invoiceId?: string;
  userName?: string;
  userEmail?: string;
  description?: string;
  planType?: string;
  provider?: string;
  paymentProvider?: string;
}

interface InvoiceRecord {
  id: number | string;
  amount: number | string;
  status?: string;
  type?: string;
  created_at?: string;
  metadata?: InvoiceMetadata | string | null;
  stripe_payment_intent_id?: string;
  deluxe_transaction_id?: string;
  stripe_customer_id?: string;
  deluxe_customer_id?: string;
  payment_provider?: string;
  gateway?: string;
}

interface SubscriptionRecord {
  id?: number | string;
  plan_type?: string;
  status?: string;
  started_at?: string;
  trial_ends_at?: string;
  gateway?: string;
}

type StatusFilter = "all" | "paid" | "pending" | "failed" | "refunded";

const ListViewInvoice: React.FC = () => {
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>([]);
  const [billingAccess, setBillingAccess] = useState<BillingAccessState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadBilling = async () => {
    setLoading(true);
    setError("");
    try {
      const [invoiceResult, subscriptionResult, accessResult] = await Promise.allSettled([
        CallAPIPostPromise("/stripe-sub/get-invoice", {}),
        CallAPIPostPromise("/stripe-sub/get-subscribers", {}),
        api.get("/stripe-sub/billing-access-state"),
      ]);
      if (invoiceResult.status === "rejected") throw invoiceResult.reason;
      setInvoices(Array.isArray(invoiceResult.value) ? invoiceResult.value as InvoiceRecord[] : []);
      setSubscriptions(subscriptionResult.status === "fulfilled" && Array.isArray(subscriptionResult.value)
        ? subscriptionResult.value as SubscriptionRecord[] : []);
      setBillingAccess(accessResult.status === "fulfilled" ? accessResult.value?.data as BillingAccessState : null);
    } catch (requestError: any) {
      setError(requestError?.message || "Your invoices could not be loaded right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadBilling(); }, []);

  const normalized = useMemo(() => invoices.map((invoice) => ({
    ...invoice,
    parsedMetadata: parseMetadata(invoice.metadata),
    normalizedStatus: normalizeStatus(invoice.status),
    numericAmount: numberValue(invoice.amount),
  })), [invoices]);

  const totals = useMemo(() => normalized.reduce((summary, invoice) => {
    summary.total += invoice.numericAmount;
    summary.count += 1;
    if (invoice.normalizedStatus === "paid") { summary.paid += invoice.numericAmount; summary.paidCount += 1; }
    if (invoice.normalizedStatus === "pending") { summary.pending += invoice.numericAmount; summary.pendingCount += 1; }
    if (invoice.normalizedStatus === "failed") summary.failedCount += 1;
    return summary;
  }, { total: 0, paid: 0, pending: 0, count: 0, paidCount: 0, pendingCount: 0, failedCount: 0 }), [normalized]);

  const filteredInvoices = useMemo(() => {
    const term = search.trim().toLowerCase();
    return normalized.filter((invoice) => {
      const id = invoiceLabel(invoice).toLowerCase();
      const description = invoiceDescription(invoice).toLowerCase();
      const matchesSearch = !term || id.includes(term) || description.includes(term) ||
        String(invoice.type || "").toLowerCase().includes(term);
      const matchesStatus = statusFilter === "all" || invoice.normalizedStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [normalized, search, statusFilter]);

  useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredInvoices.slice(start, start + pageSize);
  }, [currentPage, filteredInvoices, pageSize]);
  const rangeStart = filteredInvoices.length ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, filteredInvoices.length);

  const currentSubscription = subscriptions.find((item) => String(item.status).toLowerCase() === "active") || subscriptions[0];
  const displayedPlanStatus = billingAccess?.statusReason === "provider_subscription_missing"
    ? "Inactive at provider"
    : billingAccess?.accessLevel === "suspended"
    ? "Payment required"
    : billingAccess?.accessLevel === "grace_period"
    ? "Payment retry needed"
    : currentSubscription?.status ? titleCase(currentSubscription.status) : "Not active";
  const planIsHealthy = ["active", "trialing"].includes(String(billingAccess?.accessLevel || ""));

  const exportInvoices = () => {
    const rows = filteredInvoices.map((invoice) => [
      invoiceLabel(invoice), formatDate(invoice.created_at), invoiceDescription(invoice),
      paymentProvider(invoice) === "unknown" ? "Other" : titleCase(paymentProvider(invoice)),
      invoice.numericAmount.toFixed(2), statusLabel(invoice.normalizedStatus),
    ]);
    const csv = [["Invoice", "Date", "Description", "Provider", "Amount (USD)", "Status"], ...rows]
      .map((row) => row.map(csvCell).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `tradesmens-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-content contractor-invoices">
      <Container fluid>
        <div className="contractor-invoices__hero">
          <div>
            <span className="contractor-invoices__eyebrow"><ReceiptText size={15} /> Billing center</span>
            <h2>Invoices made simple</h2>
            <p>Review charges, check payment status, and keep your billing records organized.</p>
          </div>
          <div className="contractor-invoices__hero-actions">
            <Button variant="light" onClick={() => void loadBilling()} disabled={loading}>
              <RefreshCw size={16} className={loading ? "contractor-invoices__spin" : ""} /> Refresh
            </Button>
            <Button onClick={exportInvoices} disabled={!filteredInvoices.length}>
              <ArrowDownToLine size={16} /> Export CSV
            </Button>
          </div>
        </div>

        {error && <div className="contractor-invoices__error"><AlertCircle size={18} /><div><strong>Unable to load invoices</strong><span>{error}</span></div><Button size="sm" variant="outline-danger" onClick={() => void loadBilling()}>Try again</Button></div>}

        <Row className="g-3 contractor-invoices__summary">
          <SummaryCard title="Total billed" value={formatCurrency(totals.total)} detail={`${totals.count} billing records`} icon={<WalletCards size={20} />} tone="blue" />
          <SummaryCard title="Paid" value={formatCurrency(totals.paid)} detail={`${totals.paidCount} successful payments`} icon={<CheckCircle2 size={20} />} tone="green" />
          <SummaryCard title="Pending" value={formatCurrency(totals.pending)} detail={`${totals.pendingCount} awaiting completion`} icon={<Clock3 size={20} />} tone="amber" />
          <SummaryCard title="Failed" value={String(totals.failedCount)} detail="Payments needing attention" icon={<AlertCircle size={20} />} tone="red" />
        </Row>

        <Row className="g-3 mt-1">
          <Col xl={9}>
            <Card className="contractor-invoices__card">
              <Card.Header className="contractor-invoices__toolbar">
                <div><h5>Billing history</h5><span>{filteredInvoices.length} of {normalized.length} records</span></div>
                <div className="contractor-invoices__filters">
                  <div className="contractor-invoices__search"><Search size={16} /><Form.Control value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search invoice or description" /></div>
                  <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)} aria-label="Filter invoices by status">
                    <option value="all">All statuses</option><option value="paid">Paid</option><option value="pending">Pending</option><option value="failed">Failed</option><option value="refunded">Refunded</option>
                  </Form.Select>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {loading ? <LoadingState /> : filteredInvoices.length ? (
                  <>
                    <div className="table-responsive contractor-invoices__table-wrap">
                      <table className="table align-middle mb-0 contractor-invoices__table">
                        <thead><tr><th>Invoice</th><th>Description</th><th>Provider</th><th>Date</th><th>Amount</th><th>Status</th></tr></thead>
                        <tbody>{paginatedInvoices.map((invoice) => (
                          <tr key={String(invoice.id)}>
                            <td><div className="contractor-invoices__invoice-id"><span><FileText size={17} /></span><div><strong>{invoiceLabel(invoice)}</strong><small>Billing record</small></div></div></td>
                            <td><strong className="contractor-invoices__description">{invoiceDescription(invoice)}</strong><small className="d-block text-muted">{invoice.type ? titleCase(invoice.type) : "Account billing"}</small></td>
                            <td><ProviderBadge provider={paymentProvider(invoice)} /></td>
                            <td>{formatDate(invoice.created_at)}</td>
                            <td className="contractor-invoices__amount">{formatCurrency(invoice.numericAmount)}</td>
                            <td><StatusBadge status={invoice.normalizedStatus} /></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                    <div className="contractor-invoices__mobile-list">{paginatedInvoices.map((invoice) => <InvoiceMobileCard key={String(invoice.id)} invoice={invoice} />)}</div>
                    <div className="contractor-invoices__pagination">
                      <div className="contractor-invoices__page-size">
                        <span>Rows per page</span>
                        <Form.Select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} aria-label="Rows per page">
                          <option value={5}>5</option><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                        </Form.Select>
                      </div>
                      <span className="contractor-invoices__range">Showing {rangeStart}–{rangeEnd} of {filteredInvoices.length}</span>
                      <nav className="contractor-invoices__pager" aria-label="Invoice pagination">
                        <button type="button" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page"><ChevronLeft size={16} /><span>Previous</span></button>
                        {paginationItems(currentPage, totalPages).map((item, index) => item === "ellipsis"
                          ? <span className="contractor-invoices__ellipsis" key={`ellipsis-${index}`}>…</span>
                          : <button type="button" key={item} className={item === currentPage ? "is-active" : ""} onClick={() => setPage(item)} aria-label={`Page ${item}`} aria-current={item === currentPage ? "page" : undefined}>{item}</button>)}
                        <button type="button" onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page"><span>Next</span><ChevronRight size={16} /></button>
                      </nav>
                    </div>
                  </>
                ) : <EmptyState hasFilters={Boolean(search || statusFilter !== "all")} clear={() => { setSearch(""); setStatusFilter("all"); }} />}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3}>
            <Card className="contractor-invoices__plan-card">
              <Card.Body>
                <span className="contractor-invoices__plan-icon"><CreditCard size={21} /></span>
                <small>Current plan</small>
                <h4>{currentSubscription?.plan_type ? titleCase(currentSubscription.plan_type) : "No active plan"}</h4>
                <Badge bg={planIsHealthy ? "success-subtle" : billingAccess?.actionRequired ? "danger-subtle" : "secondary-subtle"} text={planIsHealthy ? "success" : billingAccess?.actionRequired ? "danger" : "secondary"}>
                  {displayedPlanStatus}
                </Badge>
                {currentSubscription?.started_at && <p>Started {formatDate(currentSubscription.started_at)}</p>}
                <Button as={Link as any} to="/pages-profile-settings?tag=packages#packages" variant="outline-primary" className="w-100">Manage plan</Button>
              </Card.Body>
            </Card>
            <Card className="contractor-invoices__help-card mt-3"><Card.Body><h6>Understanding a status</h6><p><strong>Paid</strong> means the charge completed. <strong>Pending</strong> may still be processing. Failed charges may need a payment-method update.</p><Button as={Link as any} to="/pages-profile-settings#financial" variant="light" className="w-100">Payment settings</Button></Card.Body></Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const SummaryCard = ({ title, value, detail, icon, tone }: { title: string; value: string; detail: string; icon: React.ReactNode; tone: string }) => <Col xl={3} md={6}><Card className={`contractor-invoices__metric contractor-invoices__metric--${tone}`}><Card.Body><div><span>{title}</span><h3>{value}</h3><small>{detail}</small></div><span className="contractor-invoices__metric-icon">{icon}</span></Card.Body></Card></Col>;

const StatusBadge = ({ status }: { status: StatusFilter }) => <Badge className={`contractor-invoices__status contractor-invoices__status--${status}`}><span />{statusLabel(status)}</Badge>;

const LoadingState = () => <div className="contractor-invoices__loading"><Spinner animation="border" size="sm" /><strong>Loading your invoices…</strong><span>This should only take a moment.</span></div>;

const paginationItems = (current: number, total: number): Array<number | "ellipsis"> => {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

  const visiblePages = Array.from(new Set([1, total, current - 1, current, current + 1]
    .filter((item) => item >= 1 && item <= total))).sort((a, b) => a - b);

  return visiblePages.reduce<Array<number | "ellipsis">>((items, item, index) => {
    if (index > 0 && item - visiblePages[index - 1] > 1) items.push("ellipsis");
    items.push(item);
    return items;
  }, []);
};

const EmptyState = ({ hasFilters, clear }: { hasFilters: boolean; clear: () => void }) => <div className="contractor-invoices__empty"><span><ReceiptText size={28} /></span><h5>{hasFilters ? "No matching invoices" : "No invoices yet"}</h5><p>{hasFilters ? "Try changing your search or status filter." : "Your billing records will appear here after your first charge."}</p>{hasFilters && <Button variant="outline-primary" size="sm" onClick={clear}>Clear filters</Button>}</div>;

const ProviderBadge = ({ provider }: { provider: "stripe" | "deluxe" | "unknown" }) => <span className={`contractor-invoices__provider contractor-invoices__provider--${provider}`}>{provider === "stripe" ? "Stripe" : provider === "deluxe" ? "Deluxe" : "Other"}</span>;

const InvoiceMobileCard = ({ invoice }: { invoice: any }) => <div className="contractor-invoices__mobile-card"><div><strong>{invoiceLabel(invoice)}</strong><StatusBadge status={invoice.normalizedStatus} /></div><p>{invoiceDescription(invoice)}</p><div className="contractor-invoices__mobile-provider"><ProviderBadge provider={paymentProvider(invoice)} /><span>{formatDate(invoice.created_at)}</span><b>{formatCurrency(invoice.numericAmount)}</b></div></div>;

function parseMetadata(value: InvoiceRecord["metadata"]): InvoiceMetadata { if (!value) return {}; if (typeof value === "object") return value; try { return JSON.parse(value); } catch (_error) { return {}; } }
function numberValue(value: number | string) { const parsed = Number(value); return Number.isFinite(parsed) ? parsed : 0; }
function normalizeStatus(value?: string): StatusFilter { const status = String(value || "").toLowerCase(); if (["succeeded", "success", "paid", "completed"].includes(status)) return "paid"; if (["failed", "declined", "canceled", "cancelled"].includes(status)) return "failed"; if (["refund", "refunded"].includes(status)) return "refunded"; return "pending"; }
function statusLabel(status: StatusFilter) { return status === "paid" ? "Paid" : status === "failed" ? "Failed" : status === "refunded" ? "Refunded" : status === "pending" ? "Pending" : "All"; }
function invoiceLabel(invoice: any) { return String(invoice.parsedMetadata?.invoiceId || `INV-${invoice.id}`).replace(/^#/, ""); }
function invoiceDescription(invoice: any) { return invoice.parsedMetadata?.description || invoice.parsedMetadata?.planType || (invoice.type ? titleCase(invoice.type) : "Tradesmens billing charge"); }
function paymentProvider(invoice: any): "stripe" | "deluxe" | "unknown" {
  const explicit = String(invoice.payment_provider || invoice.gateway || invoice.parsedMetadata?.provider || invoice.parsedMetadata?.paymentProvider || "").toLowerCase();
  if (explicit.includes("deluxe") || invoice.deluxe_transaction_id || invoice.deluxe_customer_id) return "deluxe";
  if (explicit.includes("stripe") || invoice.stripe_payment_intent_id || invoice.stripe_customer_id) return "stripe";
  return "unknown";
}
function formatCurrency(value: number) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value); }
function formatDate(value?: string) { if (!value) return "Date unavailable"; const date = new Date(value); return Number.isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
function titleCase(value: string) { return String(value || "").replace(/[_-]/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()); }
function csvCell(value: string) { return `"${String(value).replace(/"/g, '""')}"`; }

export default ListViewInvoice;
